import { Icon, SearchField } from '@telavita-core/react-design-kit'
import React from 'react'
import { Option } from '../../types/option'
import { Pagination } from 'services/medicalRecords/pagination.types'
import { SearchFieldProps } from '@telavita-core/react-design-kit/build/components/SearchField/SearchField'

type SearchSelectProps = Omit<
  SearchFieldProps,
  'value' | 'onSelectOption' | 'next' | 'onChange' | 'selectedOptions'
> & {
  selectedOption: Option[]
  onChange: (value: Option[]) => void
  loadOptions?: (query: string, page: number, isSearch?: boolean) => Promise<{
    pagination: Pagination
  }>
  onSearchChange?: (query: string) => void
  loading?: boolean
}

export const SearchSelect = ({
  selectedOption = [],
  onChange,
  loadOptions,
  options = [],
  floatingLabel,
  inputPlaceholder,
  emptyOptionsMessage,
  onSearchChange,
  loading: externalLoading,
  ...props
}: SearchSelectProps) => {
  const [pagination, setPagination] = React.useState({ total: 0, page: 0 })
  const [search, setSearch] = React.useState('')
  const [internalLoading, setInternalLoading] = React.useState(false)

  const loading = externalLoading !== undefined ? externalLoading : internalLoading

  const fetchOptions = React.useCallback(async (query: string, page: number, isSearch = false) => {
    if (!loadOptions) return
    setInternalLoading(true)
    try {
      const result = await loadOptions(query, page, isSearch)
      setPagination(result.pagination)
    } finally {
      setInternalLoading(false)
    }
  }, [loadOptions])

  const handleSearch = async (value: string) => {
    setSearch(value)
    onSearchChange?.(value)
    if (!value) {
      setSearch('')
      onChange([])
    }
  }

  React.useEffect(() => {
    fetchOptions('', 1)
  }, [fetchOptions])

  return (
    <SearchField
      id={props.id ? props.id : 'search-multi-select'}
      {...props}
      options={options}
      loading={loading}
      floatingLabel={floatingLabel}
      inputPlaceholder={inputPlaceholder}
      emptyOptionsMessage={emptyOptionsMessage}
      onSelectOption={(optionId) => {
        const newOption = options.find((op) => op.id === optionId)
        onChange(newOption ? [newOption] : [])
      }}
      clearSearchField={() => handleSearch('')}
      optionIcon={<Icon name="Plus" height={24} width={24} fill="#898989" />}
      next={() => fetchOptions(search, pagination.page + 1, false)}
      hasMore={loading || pagination.total > options.length}
      value={search}
      onChange={handleSearch}
      selectedOptions={selectedOption}
      selectedOptionIcon={<Icon name="Check" height={24} width={24} fill="#c1c1c1" />}
      closeOnSelect={true}
    />
  )
}
